import { Component, NgZone, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatSelectChange } from '@angular/material/select';
import { ApiResponse } from '@models/ApiResponse/ApiResponse';
import { TenantDto } from '@models/ApiResponse/tenant';
import { AppError } from '@models/appError';
import {
  ApiEndpointsService,
  ApiHttpErrorService,
  ApiHttpService,
  AuthenticationService,
  CustomValidatorService,
  GooglePlacesService,
  RouteNavigationService,
  ToastService
} from '@services/index';
import { Observable } from 'rxjs';
import { finalize, map, startWith, take } from 'rxjs/operators';
import { GooglePlacesComponent } from '../admin/views/google-places/google-places.component';
import { Constants } from '../config/constants';
import {
  Countries,
  IdTypes,
  Nationalities,
  StudentTypes
} from '../config/enums';

@Component({
  templateUrl: './apply.component.html',
  styleUrls: ['./apply.component.css'],
})
export class ApplyComponent implements OnInit {
  @ViewChild(GooglePlacesComponent)
  googlePlacesComponent: GooglePlacesComponent;
  registrationForm: FormGroup;
  saId = true;
  passport = false;
  hideDisabilityFileUpload = true;
  currentTenant: TenantDto;
  cvFile: any;
  disabilityFile: any;
  suburbs = this.constants.suburbs;
  cities = this.constants.cities;
  provinces = this.constants.provinces;
  filteredCities: Observable<string[]>;
  filteredSuburbs: Observable<string[]>;
  filteredProvinces: Observable<string[]>;
  applying = false;

  constructor(
    public zone: NgZone,
    public constants: Constants,
    private googlePlacesService: GooglePlacesService,
    private formBuilder: FormBuilder,
    private apiHttpService: ApiHttpService,
    private apiEndpointsService: ApiEndpointsService,
    private customValidatorService: CustomValidatorService,
    private authenticationService: AuthenticationService,
    private routeNavService: RouteNavigationService,
    private toastService: ToastService,
    private httpErrorService: ApiHttpErrorService,
    private httpService: ApiHttpService,
    private endpointsService: ApiEndpointsService
  ) {
    this.currentTenant = this.authenticationService.currentTenantValue;
  }

  ngOnInit() {
    this.initForm();
  }

  cvFileChange(event: any) {
    const file = event.target.files[0];
    this.cvFile = file;
    if (!file) {
      this.cvFilePath.patchValue(null);
    }
  }

  disabilityFileChange(event: any) {
    const file = event.target.files[0];
    this.disabilityFile = file;
    if (!file) {
      this.disabilityFilePath.patchValue(null);
    }
  }

  get personDetailsForm() {
    return this.registrationForm.get('person') as FormGroup;
  }

  get streetAddress() {
    return this.personDetailsForm.get('streetAddress') as FormGroup;
  }

  get city() {
    return this.streetAddress.get('city');
  }

  get suburb() {
    return this.streetAddress.get('suburb');
  }

  get province() {
    return this.streetAddress.get('province');
  }

  get disabilityFilePath() {
    return this.personDetailsForm.get('disabilityFilePath');
  }

  get idNumber() {
    return this.personDetailsForm.get('idNumber');
  }

  get dateOfBirth() {
    return this.personDetailsForm.get('dateOfBirth');
  }

  get cvFilePath() {
    return this.personDetailsForm.get('cvFilePath');
  }

  get email() {
    return this.personDetailsForm.get('email');
  }

  get disability() {
    return this.personDetailsForm.get('disability');
  }

  get disabilityType() {
    return this.personDetailsForm.get('disabilityType');
  }

  get disabilityComment() {
    return this.personDetailsForm.get('disabilityComment');
  }

  get idType() {
    return this.personDetailsForm.get('idType');
  }

  public login() {
    this.routeNavService.navigate('login');
  }

  public toggleDisability(event: MatCheckboxChange) {
    if (event.checked) {
      this.disabilityType.enable();
      this.disabilityComment.enable();
      this.disabilityFilePath.enable();
      this.hideDisabilityFileUpload = false;
    } else {
      this.disabilityType.patchValue(null);
      this.disabilityComment.patchValue(null);
      this.disabilityType.disable();
      this.disabilityComment.disable();
      this.disabilityFile = null;
      this.disabilityFilePath.patchValue(null);
      this.disabilityFilePath.disable();
      this.hideDisabilityFileUpload = true;
    }
  }

  public clearPlacesInput() {
    this.googlePlacesComponent.ClearInput();
  }

  public getAddress(place: any) {
    // close google places pop-up
    const btn = document.getElementById('googlePlacesButton');
    if (btn) {
      if (btn.getAttribute('aria-expanded') === 'true') {
        btn.click();
      }
    }
    // form doesnt realize it has beeen updated if zone is not iused
    this.zone.run(() => this.setAddress(place));
  }

  public setIdType(event: MatSelectChange) {
    const value = event.value;
    if (value === IdTypes.passport) {
      this.dateOfBirth.enable();
      this.idNumber.setValidators([Validators.required]);
      this.idNumber.updateValueAndValidity();
      this.passport = true;
      this.saId = false;
    } else {
      this.dateOfBirth.disable();
      this.idNumber.setValidators([
        Validators.required,
        this.customValidatorService.ValidIdNumber(),
      ]);
      this.idNumber.updateValueAndValidity();
      this.saId = true;
      this.passport = false;
    }
  }

  public tryApply() {
    if (!this.registrationForm.valid) {
      this.toastService.missingRequiredFields();
      this.registrationForm.markAllAsTouched();
      return;
    }

    this.apply();
  }

  private setAddress(place: any) {
    const streetNumber = this.googlePlacesService.GetStreetNumber(place);
    const streetName = this.googlePlacesService.GetStreet(place);
    let street: string;
    if (streetNumber && streetName) {
      street = streetNumber + ' ' + streetName;
    } else if (streetName) {
      street = streetName;
    }
    const suburb = this.googlePlacesService.GetSuburb(place);
    const city = this.googlePlacesService.GetCity(place);
    const province = this.googlePlacesService.GetProvince(place);
    const postCode = this.googlePlacesService.GetPostCode(place);

    this.streetAddress.patchValue({
      street: street ? street : '',
      suburb: suburb ? suburb : '',
      city: city ? city : '',
      province: province ? province : '',
      postCode: postCode ? postCode : '',
    });
  }

  private _filterCities(value: string): string[] {
    if (value) {
      const filterValue = value.toLowerCase();
      return this.cities.filter(
        (city) => city.toLowerCase().indexOf(filterValue) === 0
      );
    }
  }

  private _filterSuburbs(value: string): string[] {
    if (value) {
      const filterValue = value.toLowerCase();
      return this.suburbs.filter(
        (suburb) => suburb.toLowerCase().indexOf(filterValue) === 0
      );
    }
  }

  private _filterProvinces(value: string): string[] {
    if (value) {
      const filterValue = value.toLowerCase();
      return this.provinces.filter(
        (province) => province.toLowerCase().indexOf(filterValue) === 0
      );
    }
  }

  private initAutoCompletes(): void {
    this.filteredCities = this.city.valueChanges.pipe(
      startWith(''),
      map((value) => this._filterCities(value))
    );
    this.filteredSuburbs = this.suburb.valueChanges.pipe(
      startWith(''),
      map((value) => this._filterSuburbs(value))
    );
    this.filteredProvinces = this.province.valueChanges.pipe(
      startWith(''),
      map((value) => this._filterProvinces(value))
    );
  }

  private initForm() {
    this.registrationForm = this.formBuilder.group({
      type: [StudentTypes.candidate, [Validators.required]],
      person: this.getPersonDetails(),
    });
    this.initAutoCompletes();
  }

  private getPersonDetails() {
    return this.formBuilder.group({
      title: [null, [Validators.required]],
      gender: [null, [Validators.required]],
      firstName: [null, [Validators.required]],
      middleNames: null,
      lastName: [null, [Validators.required]],
      maidenName: null,
      idType: IdTypes.sa,
      idNumber: [
        null,
        [Validators.required, this.customValidatorService.ValidIdNumber()],
      ],
      dateOfBirth: [{ value: null, disabled: true }, [Validators.required]],
      homeLanguage: [null, [Validators.required]],
      nationality: [Nationalities.southAfrican, [Validators.required]],
      race: [null, [Validators.required]],
      email: [null, [Validators.required, Validators.email]],
      whatsappNumber: null,
      cellNumber: [null, [Validators.required]],
      altNumber: null,
      nextOfKinName: [null, [Validators.required]],
      nextOfKinCellNumber: [null, [Validators.required]],
      nextOfKinRelation: [null, [Validators.required]],
      disability: [false, [Validators.required]],
      disabilityType: [{ value: null, disabled: true }, [Validators.required]],
      disabilityComment: [
        { value: null, disabled: true },
        [Validators.required],
      ],
      disabilityFilePath: { value: null, disabled: true },
      streetAddress: this.getAddressDetails(),
      postalAddress: null,
      cvFilePath: null,
    });
  }

  private getAddressDetails() {
    return this.formBuilder.group({
      street: [null, [Validators.required]],
      unit: null,
      complex: null,
      suburb: [null, [Validators.required]],
      province: [null, [Validators.required]],
      city: [null, [Validators.required]],
      country: [Countries.southAfrica, [Validators.required]],
      postCode: null,
    });
  }

  private async apply() {
    this.applying = true;
    const candidate = this.registrationForm.value;
    candidate.tenantId = this.currentTenant.id;
    this.apiHttpService
      .post<ApiResponse<number>>(
        this.apiEndpointsService.registerCandidate(),
        candidate
      )
      .pipe(
        take(1),
        finalize(() => {
          this.applying = false;
        })
      )
      .subscribe(
        (res) => {
          this.toastService.showSuccess(res.message);

          if (this.cvFile !== null) {
            this.uploadCvFile(res.data);
          }

          if (this.disabilityFile !== null) {
            this.uploadDisabilityFile(res.data);
          }

          this.routeNavService.login();
        },
        (error: AppError) => {
          this.httpErrorService.handle(error);
        }
      );
  }

  private async uploadDisabilityFile(personId: number) {
    if (this.disabilityFilePath.value === null) {
      return null;
    }

    if (this.disabilityFile.size > 30000000) {
      this.toastService.showError(
        'Disability document exceeded the maximum allowed size of 30MB.'
      );
      this.applying = false;
      return;
    }

    const files = new FormData();

    files.append('fileData', this.disabilityFile);

    files.append('docType', 'disability');

    this.uploadFiles(files, this.currentTenant.id, personId)
      .pipe(
        take(1),
        finalize(() => {})
      )
      .subscribe(
        () => {},
        (error: AppError) => {
          this.httpErrorService.handle(error);
        }
      );
  }

  private async uploadCvFile(personId: number) {
    if (this.cvFilePath.value === null) {
      return null;
    }

    if (this.cvFile.size > 30000000) {
      this.toastService.showError(
        'CV document exceeded the maximum allowed size of 30MB.'
      );
      this.applying = false;
      return;
    }

    const files = new FormData();

    files.append('fileData', this.cvFile);

    files.append('docType', 'cv');

    this.uploadFiles(files, this.currentTenant.id, personId)
      .pipe(
        take(1),
        finalize(() => {})
      )
      .subscribe(
        () => {},
        (error: AppError) => {
          this.httpErrorService.handle(error);
        }
      );
  }

  private uploadFiles(
    filesToUpload: any,
    currentTenant: number,
    personId: number
  ): Observable<any> {
    return this.httpService.post(
      this.endpointsService.uploadCandidatePersonalFiles(
        currentTenant,
        personId
      ),
      filesToUpload
    );
  }
}
